import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Ship, PartyPopper, Upload, RefreshCw, Trash2, Plus, Link as LinkIcon, FileText,
} from "lucide-react";
import { useConfirm } from "@/components/useConfirm";

const NZCA_CSV =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vQcmfMcyuwXsgTV95HODlgpVXbG5YKy1GyZwCj4sRxdeR5TT7RfV7qW_xduSoKAGQ/pub?gid=502006066&single=true&output=csv";

function SiteChips({ sites, all }) {
  return (
    <span className="inline-flex gap-1">
      {(sites || []).map((s) => (
        <Badge key={s} variant="outline" className="text-[10px]">
          {all.find((a) => a.code === s)?.label || s}
        </Badge>
      ))}
    </span>
  );
}

export default function AdminEvents() {
  const [sources, setSources] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const [upName, setUpName] = useState("");
  const [upSites, setUpSites] = useState([]);
  const [feedUrl, setFeedUrl] = useState(NZCA_CSV);
  const [manualOpen, setManualOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState("ALL");
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [shown, setShown] = useState(40);
  const [manual, setManual] = useState({
    date: "", title: "", venue: "", pax: "", start_time: "", notes: "", type: "EVENT", sites: [],
  });

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: e }] = await Promise.all([
        api.get("/admin/events/sources"),
        api.get("/admin/events"),
      ]);
      setSources(s.sources || []);
      setSiteList(s.sites || []);
      setEvents(e.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load events");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (list, setList, code) =>
    setList(list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);

  const addFeed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/events/sources", {
        name: "NZ Cruise Association schedule",
        kind: "CRUISE_FEED",
        url: feedUrl,
        sites: siteList.map((s) => s.code),
      });
      await api.post(`/admin/events/sources/${data.id}/refresh`);
      toast.success("Cruise schedule connected and loaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't connect that schedule");
    } finally { setBusy(false); }
  };

  const refresh = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/sources/${id}/refresh`);
      toast.success(`${data.imported} ship arrivals loaded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally { setBusy(false); }
  };

  const removeSource = async (src) => {
    const ok = await confirm({
      title: `Delete “${src.name}”?`,
      description: "Its dates come off the roster straight away. Manually added events are kept.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/admin/events/sources/${src.id}`);
      toast.success(`Removed · ${data.events_removed} dates cleared`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a PDF, Excel sheet or CSV first");
    if (!upSites.length) return toast.error("Choose at least one location");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", upName || file.name);
    fd.append("sites", upSites.join(","));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/events/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`${data.imported} dates read from ${file.name}`);
      if (fileRef.current) fileRef.current.value = "";
      setUpName(""); setUpSites([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that file");
    } finally { setBusy(false); }
  };

  const addManual = async () => {
    if (!manual.date || !manual.title) return toast.error("Date and name are required");
    try {
      await api.post("/admin/events", { ...manual, pax: Number(manual.pax || 0) });
      toast.success("Event added");
      setManual({ date: "", title: "", venue: "", pax: "", start_time: "", notes: "", type: "EVENT", sites: [] });
      setManualOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that event");
    }
  };

  const removeEvent = async (ev) => {
    try {
      await api.delete(`/admin/events/${ev.id}`);
      setEvents((list) => list.filter((e) => e.id !== ev.id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  const cruiseFeed = sources.find((s) => s.kind === "CRUISE_FEED");
  // Cruise feeds dwarf everything else, so let an admin narrow the list down
  // rather than silently truncating it.
  const filtered = events.filter(
    (e) => (kindFilter === "ALL" || e.type === kindFilter)
      && (siteFilter === "ALL" || (e.sites || []).includes(siteFilter))
  );
  const upcoming = filtered.slice(0, shown);

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <header>
        <h1 className="font-heading text-3xl font-bold text-[#1A2744]">Events &amp; cruise ships</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Dates added here appear on the roster for that location, so managers can see
          what&apos;s in town before they choose who&apos;s on.
        </p>
      </header>

      {/* Cruise schedule */}
      <Card className="border-gray-200" data-testid="cruise-feed-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Ship className="h-5 w-5 text-[#1A2744] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-heading text-lg font-bold text-[#1A2744]">Cruise ship schedule</div>
              <p className="text-xs text-gray-500 mt-1">
                Pulled from the New Zealand Cruise Association, which covers every port.
                Lyttelton and Akaroa calls land on Christchurch; Wellington calls on Wellington.
                It refreshes itself every Monday.
              </p>
            </div>
          </div>

          {cruiseFeed ? (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-gray-50 p-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1A2744]">{cruiseFeed.name}</div>
                <div className="text-[11px] text-gray-500">
                  {cruiseFeed.row_count} arrivals ·{" "}
                  {cruiseFeed.last_refreshed
                    ? `updated ${new Date(cruiseFeed.last_refreshed).toLocaleDateString()}`
                    : "never updated"}
                  {cruiseFeed.status === "ERROR" ? (
                    <span className="text-red-600"> · {cruiseFeed.error}</span>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" disabled={busy}
                  onClick={() => refresh(cruiseFeed.id)}
                  className="min-h-[44px] sm:min-h-0"
                  data-testid="refresh-cruise-feed"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh now
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="text-red-700 min-h-[44px] sm:min-h-0"
                  onClick={() => removeSource(cruiseFeed)}
                  data-testid="delete-cruise-feed"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <Label className="text-xs">Schedule link</Label>
              <Input
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                className="min-h-[44px] text-xs"
                data-testid="cruise-feed-url"
              />
              <Button
                onClick={addFeed} disabled={busy}
                className="bg-[#1A2744] hover:bg-[#1A2744]/90 text-white min-h-[44px]"
                data-testid="connect-cruise-feed"
              >
                <LinkIcon className="h-4 w-4 mr-1" /> Connect schedule
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload events file */}
      <Card className="border-gray-200" data-testid="events-upload-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <PartyPopper className="h-5 w-5 text-[#C9A84C] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-heading text-lg font-bold text-[#1A2744]">Events schedule</div>
              <p className="text-xs text-gray-500 mt-1">
                Upload a venue&apos;s PDF, Excel sheet or CSV (for example the stadium&apos;s
                event list). It&apos;s read automatically — dates, event names and expected
                crowd — and added to the locations you pick.
              </p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">File</Label>
              <Input
                ref={fileRef} type="file"
                accept=".pdf,.xlsx,.xls,.csv"
                className="min-h-[44px]"
                data-testid="events-file-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Name it</Label>
              <Input
                value={upName} onChange={(e) => setUpName(e.target.value)}
                placeholder="One NZ Stadium events"
                className="min-h-[44px]"
                data-testid="events-file-name"
              />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Which locations?</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {siteList.map((s) => (
                <button
                  key={s.code}
                  onClick={() => toggle(upSites, setUpSites, s.code)}
                  className={`px-3 min-h-[44px] rounded-full text-xs border transition-colors ${
                    upSites.includes(s.code)
                      ? "bg-[#1A2744] text-white border-[#1A2744]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#1A2744]"
                  }`}
                  data-testid={`upload-site-${s.code}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={upload} disabled={busy}
            className="mt-4 bg-[#C9A84C] text-[#1A2744] hover:bg-[#C9A84C]/80 min-h-[44px]"
            data-testid="upload-events-file"
          >
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Reading…" : "Upload and read"}
          </Button>

          {sources.filter((s) => s.kind === "EVENTS_FILE").length > 0 && (
            <div className="mt-5 space-y-2">
              {sources.filter((s) => s.kind === "EVENTS_FILE").map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 rounded-lg bg-gray-50 p-3"
                  data-testid={`events-source-${s.id}`}
                >
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#1A2744] truncate">{s.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {s.row_count} dates · <SiteChips sites={s.sites} all={siteList} />
                    </div>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    className="text-red-700 min-h-[44px] sm:min-h-0"
                    onClick={() => removeSource(s)}
                    data-testid={`delete-source-${s.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming list + manual add */}
      <Card className="border-gray-200" data-testid="upcoming-events-card">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-heading text-lg font-bold text-[#1A2744]">Coming up</div>
              <p className="text-xs text-gray-500 mt-1">
                {filtered.length} of {events.length} dates ahead
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={kindFilter} onValueChange={(v) => { setKindFilter(v); setShown(40); }}>
                <SelectTrigger className="h-11 sm:h-9 w-[150px] text-xs" data-testid="events-kind-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Ships &amp; events</SelectItem>
                  <SelectItem value="CRUISE">Cruise ships only</SelectItem>
                  <SelectItem value="EVENT">Events only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={siteFilter} onValueChange={(v) => { setSiteFilter(v); setShown(40); }}>
                <SelectTrigger className="h-11 sm:h-9 w-[150px] text-xs" data-testid="events-site-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All locations</SelectItem>
                  {siteList.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={manualOpen} onOpenChange={setManualOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm" variant="outline"
                  className="min-h-[44px] sm:min-h-0"
                  data-testid="add-manual-event"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add one
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                <DialogHeader><DialogTitle>Add an event</DialogTitle></DialogHeader>
                <div className="space-y-3 overflow-y-auto flex-1 pr-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Date</Label>
                      <Input
                        type="date" value={manual.date}
                        onChange={(e) => setManual({ ...manual, date: e.target.value })}
                        className="min-h-[44px]" data-testid="manual-event-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Kind</Label>
                      <Select value={manual.type} onValueChange={(v) => setManual({ ...manual, type: v })}>
                        <SelectTrigger className="min-h-[44px]" data-testid="manual-event-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EVENT">Event</SelectItem>
                          <SelectItem value="CRUISE">Cruise ship</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={manual.title}
                      onChange={(e) => setManual({ ...manual, title: e.target.value })}
                      placeholder="Crusaders v Blues"
                      className="min-h-[44px]" data-testid="manual-event-title"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Venue</Label>
                      <Input
                        value={manual.venue}
                        onChange={(e) => setManual({ ...manual, venue: e.target.value })}
                        className="min-h-[44px]" data-testid="manual-event-venue"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">People expected</Label>
                      <Input
                        type="number" inputMode="numeric" value={manual.pax}
                        onChange={(e) => setManual({ ...manual, pax: e.target.value })}
                        className="min-h-[44px]" data-testid="manual-event-pax"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={manual.notes}
                      onChange={(e) => setManual({ ...manual, notes: e.target.value })}
                      rows={2} data-testid="manual-event-notes"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Locations</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {siteList.map((s) => (
                        <button
                          key={s.code}
                          onClick={() => setManual({
                            ...manual,
                            sites: manual.sites.includes(s.code)
                              ? manual.sites.filter((c) => c !== s.code)
                              : [...manual.sites, s.code],
                          })}
                          className={`px-3 min-h-[44px] rounded-full text-xs border ${
                            manual.sites.includes(s.code)
                              ? "bg-[#1A2744] text-white border-[#1A2744]"
                              : "bg-white text-gray-600 border-gray-200"
                          }`}
                          data-testid={`manual-site-${s.code}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-3">
                  <Button
                    onClick={addManual}
                    className="bg-[#1A2744] hover:bg-[#1A2744]/90 text-white min-h-[44px] w-full sm:w-auto"
                    data-testid="save-manual-event"
                  >
                    Add event
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          <div className="mt-4 divide-y divide-gray-100">
            {upcoming.length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center">
                Nothing scheduled yet. Connect the cruise schedule or upload an events file.
              </p>
            )}
            {upcoming.map((ev) => (
              <div
                key={ev.id}
                className="flex items-center gap-3 py-2.5"
                data-testid={`event-row-${ev.id}`}
              >
                {ev.type === "CRUISE"
                  ? <Ship className="h-4 w-4 text-[#1A2744] shrink-0" />
                  : <PartyPopper className="h-4 w-4 text-[#C9A84C] shrink-0" />}
                <div className="w-24 shrink-0 text-xs text-gray-600">
                  {new Date(ev.date + "T00:00:00").toLocaleDateString("en-NZ",
                    { day: "numeric", month: "short", year: "2-digit" })}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#1A2744] truncate">{ev.title}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1.5">
                    <span>
                      {[ev.venue, ev.pax ? `${ev.pax.toLocaleString()} people` : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <SiteChips sites={ev.sites} all={siteList} />
                  </div>
                </div>
                <Button
                  size="sm" variant="ghost"
                  className="text-red-700 shrink-0 min-h-[44px] sm:min-h-0"
                  onClick={() => removeEvent(ev)}
                  data-testid={`delete-event-${ev.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {filtered.length > upcoming.length && (
              <div className="pt-3 text-center">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setShown((n) => n + 60)}
                  className="min-h-[44px] sm:min-h-0"
                  data-testid="events-show-more"
                >
                  Show more ({filtered.length - upcoming.length} to go)
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
